import { AdminQrRiskLogTable } from './components/AdminQrRiskLogTable';
import { AdminQrRiskLogGroupedTable } from './components/AdminQrRiskLogGroupedTable';
import { RiskWhitelistDomainsDialog } from './components/RiskWhitelistDomainsDialog';
import { GET_ADMIN_QR_RISK_LOG_QUERY } from '@app/constants/query-api-configs';
import { SearchInput } from '@app/components/fields/search-input/SearchInput';
import { useQueryParam, withDefault, StringParam } from 'use-query-params';
import { Page } from '@app/hoc';
import { Button, OptionItem, Select } from '@app/components';
import { SelectChangeEvent } from '@mui/material';
import { oneOfEnum } from '@app/utils/yup.utils';
import { EAccountRiskCategory, EQrCodeSourceAction, EWebRiskVerdict } from '@app/swagger-types';
import { clsxm } from '@app/styles/clsxm';
import NiceModal from '@ebay/nice-modal-react';

export const AdminQrRiskLogListPage: React.FC = () => {
  const [qrCodeId, setQrCodeId] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.qrCodeIdParam,
    withDefault(StringParam, '')
  );
  const [wouldBlock, setWouldBlock] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.wouldBlockParam,
    withDefault(StringParam, '')
  );
  const [accountCategory, setAccountCategory] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.accountCategoryParam,
    withDefault(StringParam, '')
  );
  const [sourceAction, setSourceAction] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.sourceActionParam,
    withDefault(StringParam, '')
  );
  const [webRiskVerdict, setWebRiskVerdict] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.webRiskVerdictParam,
    withDefault(StringParam, '')
  );
  // Default 'false' (open only) - Danny's feedback (2026-08-26): old, already-looked-at
  // findings were piling up forever with no way to clear them.
  const [reviewed, setReviewed] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.reviewedParam,
    withDefault(StringParam, 'false')
  );
  // Default 'grouped' - one row per QR code, not one per flag event (the "dubbel vermeld"
  // feedback). 'raw' is the original per-event log, reached via a grouped row's History button.
  const [view, setView] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.viewParam, withDefault(StringParam, 'grouped'));

  const isGrouped = view !== 'raw';

  return (
    <Page>
      <div className="flex items-end flex-wrap lg:flex-nowrap justify-start lg:justify-end gap-2 p-5">
        <div className="text-xs italic text-gray-400">
          Shadow mode: nothing here is blocked yet. &quot;Would block&quot; shows what enforce=true would flag.
        </div>
        <Button
          size="small"
          variant="outlined"
          onClick={() => NiceModal.show(RiskWhitelistDomainsDialog)}
          className="ml-auto lg:ml-0"
        >
          Whitelist
        </Button>
        <div className="flex gap-1">
          <Button
            size="small"
            variant={isGrouped ? 'contained' : 'outlined'}
            onClick={() => setView('grouped')}
            className={clsxm({ 'font-bold': isGrouped })}
          >
            Grouped
          </Button>
          <Button
            size="small"
            variant={!isGrouped ? 'contained' : 'outlined'}
            onClick={() => setView('raw')}
            className={clsxm({ 'font-bold': !isGrouped })}
          >
            Raw history
          </Button>
        </div>
        <Select
          size="small"
          inputProps={{ label: 'Status' }}
          displayEmpty
          value={reviewed ?? ''}
          onChange={(e: SelectChangeEvent<string>) => setReviewed(e.target.value || undefined)}
        >
          <OptionItem value={''}>All</OptionItem>
          <OptionItem value={'false'}>Open</OptionItem>
          <OptionItem value={'true'}>Reviewed</OptionItem>
        </Select>
        <Select
          size="small"
          inputProps={{ label: 'Would block' }}
          displayEmpty
          value={wouldBlock || ''}
          onChange={(e: SelectChangeEvent<string>) => setWouldBlock(e.target.value || undefined)}
        >
          <OptionItem value={''}>All</OptionItem>
          <OptionItem value={'true'}>Would block</OptionItem>
          <OptionItem value={'false'}>Clean</OptionItem>
        </Select>
        <Select
          size="small"
          inputProps={{ label: 'Account type' }}
          displayEmpty
          value={accountCategory || ''}
          onChange={(e: SelectChangeEvent<string>) => {
            const val = e.target.value;
            setAccountCategory(oneOfEnum(EAccountRiskCategory).isValidSync(val) ? val : undefined);
          }}
        >
          <OptionItem value={''}>All</OptionItem>
          {Object.values(EAccountRiskCategory).map((key) => (
            <OptionItem value={key} key={key}>
              {key}
            </OptionItem>
          ))}
        </Select>
        <Select
          size="small"
          inputProps={{ label: 'Source' }}
          displayEmpty
          value={sourceAction || ''}
          onChange={(e: SelectChangeEvent<string>) => {
            const val = e.target.value;
            setSourceAction(oneOfEnum(EQrCodeSourceAction).isValidSync(val) ? val : undefined);
          }}
        >
          <OptionItem value={''}>All</OptionItem>
          {Object.values(EQrCodeSourceAction).map((key) => (
            <OptionItem value={key} key={key}>
              {key}
            </OptionItem>
          ))}
        </Select>
        <Select
          size="small"
          inputProps={{ label: 'Web Risk verdict' }}
          displayEmpty
          value={webRiskVerdict || ''}
          onChange={(e: SelectChangeEvent<string>) => {
            const val = e.target.value;
            setWebRiskVerdict(oneOfEnum(EWebRiskVerdict).isValidSync(val) ? val : undefined);
          }}
        >
          <OptionItem value={''}>All</OptionItem>
          {Object.values(EWebRiskVerdict).map((key) => (
            <OptionItem value={key} key={key}>
              {key}
            </OptionItem>
          ))}
        </Select>
        <SearchInput value={qrCodeId} onChange={setQrCodeId} label="QR code id" placeholder="QR code id" />
      </div>
      <div className="p-5">
        {isGrouped ? (
          <AdminQrRiskLogGroupedTable
            onViewHistory={(code) => {
              setQrCodeId(code);
              setView('raw');
            }}
          />
        ) : (
          <AdminQrRiskLogTable />
        )}
      </div>
    </Page>
  );
};
